/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import axios from 'axios';
import { api } from './api';
import { Debate, DebateView, Message } from '../types';
import { sendPusherMessage, getDebateChannelName } from './pusherService';
import logger from '../utils/logger';

// Since we're using the api instance with baseURL already set to include /api,
// we don't need to add /api again in the path
const makeApiPath = (path: string) => {
  // Just return the path as-is, the api instance will handle the base URL
  return path.startsWith('/') ? path : '/' + path;
};

// Get all debates
export const getDebates = async (): Promise<Debate[]> => {
  try {
    logger.info(`Making API request to ${makeApiPath('/debates')}`);
    const response = await api.get(makeApiPath('/debates'));
    logger.info('Got response for all debates:', response.data);
    
    // Validate response data before returning
    if (!Array.isArray(response.data)) {
      logger.error('Invalid response format for debates - expected array but got:', typeof response.data);
      return [];
    }
    
    // Map MongoDB _id to id if needed and filter out invalid debates
    const validDebates = response.data.map(debate => {
      // If debate has _id but no id, map _id to id
      if (debate && debate._id && !debate.id) {
        logger.info(`Mapping _id to id for debate: ${debate._id}`);
        return { ...debate, id: debate._id };
      }
      return debate;
    }).filter(debate => {
      if (!debate || (!debate.id && !debate._id)) {
        logger.warn('Found invalid debate in response:', debate);
        return false;
      }
      return true;
    });
    
    logger.info(`Returning ${validDebates.length} valid debates`);
    return validDebates;
  } catch (error) {
    logger.error('Error fetching debates:', error);
    throw error;
  }
};

// Get a single debate by ID
export const getDebateById = async (id: string): Promise<Debate> => {
  try {
    logger.info(`Making API request to ${makeApiPath(`/debates/${id}`)}`);
    const response = await api.get(makeApiPath(`/debates/${id}`));
    logger.info(`Got response for debate ${id}:`, response.data);
    
    // Map MongoDB _id to id if needed
    if (response.data && response.data._id && !response.data.id) {
      logger.info(`Mapping _id to id for debate detail: ${response.data._id}`);
      response.data.id = response.data._id;
    }
    
    return response.data;
  } catch (error) {
    logger.error(`Error fetching debate ${id}:`, error);
    throw error;
  }
};

// Create a new debate
export const createDebate = async (debateData: {
  topic: string;
  description?: string;
  parameters?: {
    numberOfTeams: number;
    timeLimit?: number;
    maxMessages?: number;
  };
  teams?: {
    name: string;
    viewpoint: string;
    isAI: boolean;
    maxMembers?: number;
    publicSpots?: number;
  }[];
  views?: {
    prompt: string;
    counterPrompt: string;
  }[];
  isPublic?: boolean;
  publicJoinable?: boolean;
}): Promise<Debate> => {
  try {
    logger.info(`Making API request to ${makeApiPath('/debates')} with data:`, debateData);
    const response = await api.post(makeApiPath('/debates'), debateData);
    logger.info('Got response for create debate:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Error creating debate:', error);
    throw error;
  }
};

// Generate debate views (AI call — needs longer than default 10s axios timeout)
export const generateDebateViews = async (topic: string, description?: string): Promise<DebateView> => {
  try {
    const response = await api.post(
      makeApiPath('/debates/generate-views'),
      { topic, description },
      { timeout: 60000 }
    );
    return response.data;
  } catch (error) {
    logger.error('Error generating debate views:', error);
    throw error;
  }
};

// Update a debate
export const updateDebate = async (id: string, updateData: Partial<Debate>): Promise<Debate> => {
  try {
    const response = await api.put(makeApiPath(`/debates/${id}`), updateData);
    return response.data;
  } catch (error) {
    logger.error(`Error updating debate ${id}:`, error);
    throw error;
  }
};

// Delete a debate
export const deleteDebate = async (id: string): Promise<void> => {
  try {
    await api.delete(makeApiPath(`/debates/${id}`));
  } catch (error) {
    logger.error(`Error deleting debate ${id}:`, error);
    throw error;
  }
};

// Export debate as JSON
export const exportDebateAsJson = async (id: string): Promise<Blob> => {
  try {
    const debate = await getDebateById(id);
    const jsonString = JSON.stringify(debate, null, 2);
    return new Blob([jsonString], { type: 'application/json' });
  } catch (error) {
    logger.error(`Error exporting debate ${id}:`, error);
    throw error;
  }
};

// Export debate as PDF (this would typically call a backend endpoint)
export const exportDebateAsPdf = async (id: string): Promise<Blob> => {
  try {
    const response = await api.get(makeApiPath(`/debates/${id}/export/pdf`), {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    logger.error(`Error exporting debate ${id} as PDF:`, error);
    throw error;
  }
};

// Join an existing team in a debate
export const joinTeam = async (debateId: string, teamId: string): Promise<any> => {
  try {
    logger.info(`Making API request to join team: ${makeApiPath(`/debates/${debateId}/teams/${teamId}/join`)}`);
    const response = await api.post(makeApiPath(`/debates/${debateId}/teams/${teamId}/join`), {});
    logger.info('Got response for join team:', response.data);
    return response.data;
  } catch (error) {
    logger.error(`Error joining team in debate ${debateId}:`, error);
    throw error;
  }
};

// Get all active debates
export const getActiveDebates = async (): Promise<Debate[]> => {
  try {
    logger.info(`Making API request to ${makeApiPath('/debates/active')}`);
    const response = await api.get(makeApiPath('/debates/active'));
    logger.info('Got response for active debates:', response.data);
    
    // Validate response data before returning
    if (!Array.isArray(response.data)) {
      logger.error('Invalid response format for active debates - expected array but got:', typeof response.data);
      return [];
    }
    
    // Map MongoDB _id to id if needed and filter out invalid debates
    const validDebates = response.data
      .map(debate => {
        // If debate has _id but no id, map _id to id
        if (debate && debate._id && !debate.id) {
          logger.info(`Mapping _id to id for debate: ${debate._id}`);
          return { ...debate, id: debate._id };
        }
        return debate;
      })
      .filter(debate => {
        if (!debate || (!debate.id && !debate._id)) {
          logger.warn('Found invalid debate in active debates response:', debate);
          return false;
        }
        return true;
      });
    
    logger.info(`Returning ${validDebates.length} valid active debates`);
    return validDebates;
  } catch (error) {
    logger.error('Error fetching active debates:', error);
    // If it's a 401 error, just return an empty array - this is expected for unauthenticated users
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      logger.info('Returning empty array for unauthenticated request');
      return [];
    }
    throw error;
  }
};

// Get all completed debates
export const getCompletedDebates = async (): Promise<Debate[]> => {
  try {
    logger.info(`Making API request to ${makeApiPath('/debates/completed')}`);
    const response = await api.get(makeApiPath('/debates/completed'));
    logger.info('Got response for completed debates:', response.data);
    
    // Validate response data before returning
    if (!Array.isArray(response.data)) {
      logger.error('Invalid response format for completed debates - expected array but got:', typeof response.data);
      return [];
    }
    
    // Map MongoDB _id to id if needed and filter out invalid debates
    const validDebates = response.data
      .map(debate => {
        // If debate has _id but no id, map _id to id
        if (debate && debate._id && !debate.id) {
          logger.info(`Mapping _id to id for debate: ${debate._id}`);
          return { ...debate, id: debate._id };
        }
        return debate;
      })
      .filter(debate => {
        if (!debate || (!debate.id && !debate._id)) {
          logger.warn('Found invalid debate in completed debates response:', debate);
          return false;
        }
        return true;
      });
    
    logger.info(`Returning ${validDebates.length} valid completed debates`);
    return validDebates;
  } catch (error) {
    logger.error('Error fetching completed debates:', error);
    // If it's a 401 error, just return an empty array - this is expected for unauthenticated users
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      logger.info('Returning empty array for unauthenticated request');
      return [];
    }
    throw error;
  }
};

// Update debate status (active/completed) and trigger summary generation
export const updateDebateStatus = async (debateId: string, status: string, winner?: string, winnerDeterminationReason?: string): Promise<any> => {
  try {
    const response = await api.put(makeApiPath(`/debates/${debateId}/status`), { 
      status, 
      winner,
      winnerDeterminationReason 
    });
    return response.data;
  } catch (error) {
    // Treat 409 as benign: another client already completed it
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      logger.warn('Debate already completed by another client (409)');
      return { message: 'Already completed' };
    }
    logger.error('Error updating debate status:', error);
    throw error;
  }
};

// User personal notes
export const getUserNotes = async (debateId: string): Promise<string> => {
  try {
    const response = await api.get(makeApiPath(`/debates/${debateId}/notes`));
    return response.data?.notes || '';
  } catch (error) {
    logger.error('Error fetching user notes:', error);
    throw error;
  }
};

export const saveUserNotes = async (debateId: string, notes: string): Promise<void> => {
  try {
    await api.put(makeApiPath(`/debates/${debateId}/notes`), { notes });
  } catch (error) {
    logger.error('Error saving user notes:', error);
    throw error;
  }
};

// Get user's debates (created or invited to)
export const getUserDebates = async (userId: string): Promise<Debate[]> => {
  try {
    logger.info(`Making API request to ${makeApiPath(`/debates/user/${userId}`)}`);
    const response = await api.get(makeApiPath(`/debates/user/${userId}`));
    logger.info('Got response for user debates:', response.data);
    
    // Validate response data before returning
    if (!Array.isArray(response.data)) {
      logger.error('Invalid response format for user debates - expected array but got:', typeof response.data);
      return [];
    }
    
    // Map MongoDB _id to id if needed and filter out invalid debates
    const validDebates = response.data
      .map(debate => {
        // If debate has _id but no id, map _id to id
        if (debate && debate._id && !debate.id) {
          logger.info(`Mapping _id to id for debate: ${debate._id}`);
          return { ...debate, id: debate._id };
        }
        return debate;
      })
      .filter(debate => {
        if (!debate || (!debate.id && !debate._id)) {
          logger.warn('Found invalid debate in user debates response:', debate);
          return false;
        }
        return true;
      });
    
    logger.info(`Returning ${validDebates.length} valid user debates`);
    return validDebates;
  } catch (error) {
    logger.error('Error fetching user debates:', error);
    if (axios.isAxiosError(error)) {
      logger.error('Request details:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data
      });
    }
    throw error;
  }
};

// Get team invite details
export const getTeamInviteDetails = async (inviteCode: string): Promise<any> => {
  try {
    const response = await api.get(makeApiPath(`/debates/teams/invite/${inviteCode}`));
    return response.data;
  } catch (error) {
    logger.error('Error fetching team invite details:', error);
    throw error;
  }
};

// Join a team using an invite code
export const joinTeamByInviteCode = async (inviteCode: string, userId?: string, invitePassword?: string, captchaToken?: string): Promise<any> => {
  try {
    const response = await api.post(
      makeApiPath(`/debates/teams/join/${inviteCode}`),
      { userId, ...(invitePassword ? { invitePassword } : {}), ...(captchaToken ? { captchaToken } : {}) }
    );
    return response.data;
  } catch (error) {
    logger.error('Error joining team with invite code:', error);
    throw error;
  }
};

// Update sendMessage to use Pusher
export const sendMessage = async (debateId: string, content: string, teamId: string, isReformulation: boolean = false): Promise<Message> => {
  try {
    // Get current user ID from localStorage
    const userInfo = localStorage.getItem('user');
    const userId = userInfo ? JSON.parse(userInfo).id : null;
    
    logger.info(`Sending message to debate ${debateId} from team ${teamId}, user ${userId}, reformulation: ${isReformulation}`);
    
    // Create basic message data
    const messageData = {
      content,
      teamId,
      userId, // Include userId which is required by the server
      isReformulation
    };
    
    // Send to API to store in database - use makeApiPath to ensure proper routing
    logger.info(`🔊 KID A SENDING: I'm sending a message to the tower!`, {
      endpoint: makeApiPath(`/debates/${debateId}/messages`),
      content: messageData.content.substring(0, 30) + (messageData.content.length > 30 ? '...' : ''),
      teamId: messageData.teamId,
      userId: messageData.userId,
      isReformulation: messageData.isReformulation
    });
    const response = await api.post(makeApiPath(`/debates/${debateId}/messages`), messageData);
    logger.info(`🔊 KID A RESPONSE: The tower confirmed it received my message!`, {
      success: !!response.data,
      messageId: response.data?.message?.id || response.data?.id,
      status: response.status
    });
    const message = response.data.message;
    
    // Add debugging - log the message structure
    logger.info('🔄 Message received from API:', {
      id: message.id || message._id,
      contentPreview: message.content?.substring(0, 30),
      hasDebateId: !!message.debateId
    });
    
    // Send via Pusher for real-time updates (try to catch and handle errors)
    try {
      // Always ensure the message has the debateId (critical for routing)
      const enhancedMessage = {
        ...message,
        debateId: debateId // Make absolutely sure debateId is included
      };
      
      logger.info('🔄 Sending message via Pusher:', { 
        channelName: getDebateChannelName(debateId),
        id: enhancedMessage.id, 
        debateId: enhancedMessage.debateId
      });
      
      // Send via Pusher channel
      await sendPusherMessage(
        getDebateChannelName(debateId),
        'message_received',
        enhancedMessage
      );
      
      logger.info('🔄 Successfully sent message via Pusher');
    } catch (pusherError) {
      logger.error('🔄 Error sending message via Pusher:', pusherError);
      logger.info('🔄 Message was still saved in database despite Pusher error');
      // Don't re-throw - the message was saved in the database
    }
    
    logger.info('Message sent successfully:', message);
    return message;
  } catch (error) {
    logger.error('Error sending message:', error);
    throw error;
  }
}; 